import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects } from '../api/queries/projects';
import { useTimeEntries, useServiceRates, effectiveRates } from '../api/queries/time-entries';
import { useExpenses } from '../api/queries/expenses';
import { useMatters } from '../api/queries/matters';
import { useCreateInvoice, useInvoiceDetail, previewInvoicePdf } from '../api/queries/invoices';
import { ApiError } from '../api/client';
import { DateInput } from '../components/DateInput';
import { MoneyInput } from '../components/MoneyInput';
import { IconAction } from '../components/IconAction';
import { ProjectOptions } from '../components/ProjectOptions';
import { Toggle } from '../components/Toggle';
import {
  DocumentPreviewModal,
  type PreviewableFile,
} from '../components/DocumentPreviewModal';
import { fmtDate } from '../utils/date';
import { apiErrorText } from '../utils/apiErrors';
import { NET_DAYS, dueDateFor } from '../utils/invoiceTerms';
import { partitionByClosed } from '../utils/project';
import type {
  CreateInvoiceInput,
  InvoiceDetailResponse,
  InvoiceLineInput,
  Project,
  ServiceRate,
  TimeEntry,
} from '@stfors/shared-types';
import { matterFilterMatches, type MatterFilter } from '../utils/matter';
import { entryBilling, fmtEntryHours } from '../utils/time-entry';
import './NewInvoicePage.css';

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

import { formatMoney } from '../utils/money';

interface ManualLine {
  description: string;
  quantity: string;
  unit_amount: string;
  // Set for flat-fee-from-rate lines so the server can snapshot the Global
  // baseline for a discount. Undefined for free-text manual lines.
  service_rate_name?: string;
}

export function NewInvoicePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectsQ = useProjects();

  const initialProjectId = (() => {
    const v = searchParams.get('project_id');
    if (!v) return 0;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : 0;
  })();

  // 0 = no project chosen yet (renders the picker below).
  const [projectId, setProjectId] = useState<number>(initialProjectId);
  // ?matter_id= from a matter's "New invoice" on the project page (0061).
  const initialMatterId = (() => {
    const n = Number(searchParams.get('matter_id'));
    return Number.isInteger(n) && n > 0 ? n : null;
  })();

  // ---- Duplicate and edit (?from=<invoice id>) ----
  // The copy picks its source's project once it loads (a client-level invoice
  // has none, so the picker stays up), and the builder starts from its lines.
  const fromId = (() => {
    const n = Number(searchParams.get('from'));
    return Number.isInteger(n) && n > 0 ? n : null;
  })();
  const sourceQ = useInvoiceDetail(fromId);
  const source = sourceQ.data?.invoice ?? null;
  const pickedFromSource = useRef(false);
  useEffect(() => {
    if (!source || pickedFromSource.current) return;
    pickedFromSource.current = true;
    if (source.project_id !== null) setProjectId(source.project_id);
  }, [source]);
  // You bill live matters; a closed one is the exception, so it is off by
  // default and one click away.
  const [showClosed, setShowClosed] = useState(false);

  if (projectsQ.isPending) return <p className="niv-status">Loading projects…</p>;
  if (fromId !== null && sourceQ.isPending) {
    return <p className="niv-status">Loading the invoice to copy…</p>;
  }
  if (projectsQ.error) {
    return <p className="niv-status niv-error">Could not load projects.</p>;
  }

  const projects = projectsQ.data?.projects ?? [];
  // Resolved against the FULL list, not the filtered one: /invoices/new?project_id=N
  // for a closed matter must still open its builder rather than claim the
  // project doesn't exist.
  const selectedProject = projectId > 0 ? projects.find((p) => p.id === projectId) ?? null : null;
  const { active, closed } = partitionByClosed(projects);
  const offered = showClosed ? projects : active;

  return (
    <section className="page-stack page-stack-wide">
      <div className="card niv-toolbar">
        <header className="niv-header">
          <h2>{source ? 'Duplicate Invoice' : 'New Invoice'}</h2>
          <button type="button" className="btn-text" onClick={() => navigate('/invoices')}>
            ← Back to invoices
          </button>
        </header>
        {fromId !== null && (
          <p className={`niv-dup-note${sourceQ.error ? ' niv-error' : ''}`} role="status">
            {sourceQ.error
              ? apiErrorText(sourceQ.error, 'Could not load the invoice to copy.')
              : `Copied from ${source!.invoice_number}. Its lines come over as manual lines — the time `
                + 'and expenses they billed stay on the original, since each can only be billed once. '
                + 'The dates are new; change anything, then create it as a new draft.'}
          </p>
        )}
      </div>

      {selectedProject === null ? (
        <div className="card">
          <div className="niv-section-head">
            <h3 className="niv-section-title">Project</h3>
            {closed.length > 0 && (
              <Toggle
                checked={showClosed}
                onChange={setShowClosed}
                label="Show inactive"
                title="Include closed and archived projects"
              />
            )}
          </div>
          <label className="niv-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(Number(e.target.value))}
              autoFocus
            >
              <option value={0}>Select a project…</option>
              <ProjectOptions projects={offered} />
            </select>
          </label>
          {projectId > 0 && (
            <p className="niv-status niv-error">That project could not be found. Pick another.</p>
          )}
        </div>
      ) : (
        <InvoiceBuilder
          // A different project starts a fresh builder — selections and the
          // matter filter belong to the project they were made on.
          key={selectedProject.id}
          project={selectedProject}
          onChangeProject={() => setProjectId(0)}
          copyFrom={source}
          initialMatterId={
            selectedProject.id === initialProjectId ? initialMatterId : null
          }
        />
      )}
    </section>
  );
}

function InvoiceBuilder({
  project,
  onChangeProject,
  copyFrom = null,
  initialMatterId = null,
}: {
  project: Project;
  onChangeProject: () => void;
  /** "Duplicate and edit": the invoice whose lines and settings to start from. */
  copyFrom?: InvoiceDetailResponse['invoice'] | null;
  /** Open limited to this matter, with its unbilled work selected (0061). */
  initialMatterId?: number | null;
}): JSX.Element {
  const navigate = useNavigate();
  const projectId = project.id;
  const mattersQuery = useMatters(projectId);
  const matters = mattersQuery.data?.matters ?? [];
  // Which of the project's work this invoice is for. A specific matter is
  // also sent as the invoice's matter_id, so its name prints on the PDF and
  // the API refuses anything from outside it; "all" and "none" only filter.
  const [matterFilter, setMatterFilter] = useState<MatterFilter>(
    () => initialMatterId ?? copyFrom?.matter_id ?? 'all',
  );

  const timeEntriesQuery = useTimeEntries(projectId);
  const expensesQuery = useExpenses(projectId);
  const serviceRatesQuery = useServiceRates(projectId);
  const createInvoice = useCreateInvoice();

  const [selectedTimeIds, setSelectedTimeIds] = useState<Set<number>>(new Set());
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<number>>(new Set());
  // A copy's lines become MANUAL lines. Its time entries and expenses are
  // billed on the original, and migration 0039 lets a source be on only one
  // live line — so re-linking them would be refused, and shouldn't be wanted.
  const [manualLines, setManualLines] = useState<ManualLine[]>(() =>
    (copyFrom?.line_items ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((l) => ({
        description: l.description,
        quantity: l.quantity,
        unit_amount: l.unit_amount,
        ...(l.service_rate_name ? { service_rate_name: l.service_rate_name } : {}),
      })));
  // Per-line price overrides, keyed by time-entry id. Empty = bill at the
  // project's effective rate.
  const [rateOverrides, setRateOverrides] = useState<Record<number, string>>({});
  // Which row's rate is open for editing. A money box in every row invites the
  // stray click it sits next to — overriding a rate is deliberate, so it takes
  // a deliberate act. One at a time; opening another closes the first.
  const [editingRateId, setEditingRateId] = useState<number | null>(null);
  const [issueDate, setIssueDate] = useState<number | null>(() => todayEpoch());
  const [dueDate, setDueDate] = useState<number | null>(() => dueDateFor(todayEpoch()));
  // The due date follows the issue date at Net 30 until someone sets it by
  // hand — after which it is their number and moving the issue date must not
  // silently overwrite it.
  const [dueTouched, setDueTouched] = useState(false);
  const onChangeIssueDate = (next: number | null) => {
    setIssueDate(next);
    if (!dueTouched) setDueDate(dueDateFor(next));
  };
  const [taxAmount, setTaxAmount] = useState(() =>
    copyFrom && Number(copyFrom.tax_amount) > 0 ? copyFrom.tax_amount : '');
  const [notes, setNotes] = useState(() => copyFrom?.notes ?? '');
  // Per invoice, not per firm: showing a client the discount they negotiated is
  // worth doing on that engagement and noise on the one where they didn't.
  const [showDiscount, setShowDiscount] = useState(() => copyFrom?.show_discount ?? true);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewableFile | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const toggleTimeId = (id: number) => {
    setSelectedTimeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpenseId = (id: number) => {
    setSelectedExpenseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Enter/Space on a focused row does what clicking it does — the checkbox
  // these rows replaced was keyboard-reachable, and losing that would be a
  // regression rather than a simplification.
  const rowKeyHandler = (toggle: () => void) => (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault(); // Space would scroll the page
    toggle();
  };

  const addManualLine = () => {
    setManualLines((prev) => [...prev, { description: '', quantity: '1', unit_amount: '0' }]);
  };
  const removeManualLine = (idx: number) => {
    setManualLines((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateManualLine = (idx: number, patch: Partial<ManualLine>) => {
    setManualLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addFlatFeeLine = (rate: ServiceRate) => {
    setManualLines((prev) => [
      ...prev,
      { description: rate.name, quantity: '1', unit_amount: rate.amount, service_rate_name: rate.name },
    ]);
  };

  if (timeEntriesQuery.isPending || expensesQuery.isPending || serviceRatesQuery.isPending) {
    return (
      <div className="card">
        <p className="niv-status">Loading project billing data…</p>
      </div>
    );
  }

  // Unbilled time entries only, within the chosen matter.
  const timeEntries = (timeEntriesQuery.data?.time_entries ?? []).filter(
    (te) => te.invoice_id === null && matterFilterMatches(matterFilter, te.matter_id),
  );
  // Reimbursable, approved, not-yet-billed expenses only, within the matter.
  const expenses = (expensesQuery.data?.expenses ?? []).filter(
    (ex) => ex.reimbursable && ex.approved_at != null && !ex.billed
      && matterFilterMatches(matterFilter, ex.matter_id ?? null),
  );
  // The project's effective sheet — project override, else client, else global
  // (the API returns them ranked; effectiveRates keeps the first per name).
  const effective = effectiveRates(serviceRatesQuery.data?.service_rates ?? []);
  const flatFeeRates = effective.filter((r) => r.rate_type === 'flat_fee');

  /**
   * What a time entry actually bills at.
   *
   * The entry stores a hard `service_rate_id`, so work logged before a project
   * override existed still points at the Global row. The API prices from the
   * effective sheet; this mirrors that so the table, the live total and the
   * invoice agree — see resolveInvoiceLines.
   *
   * A flat-fee entry with its own fee bills that fee; its quantity is 1, not
   * its hours (timeEntryBilling, migration 0057).
   */
  const billedRate = (te: TimeEntry): string => {
    const rateType = te.service_rate_type ?? 'hourly';
    const match = te.service_rate_name
      ? effective.find((r) => r.name === te.service_rate_name && r.rate_type === rateType)
      : undefined;
    return entryBilling(te, match?.amount ?? te.service_rate_amount ?? '0').unit_amount;
  };
  const rateFor = (te: TimeEntry): string => rateOverrides[te.id] ?? billedRate(te);
  const quantityOf = (te: TimeEntry): number => Number(entryBilling(te).quantity);

  // Live, client-side subtotal preview — display only. The server
  // recomputes everything authoritatively on create.
  const timeSubtotal = timeEntries
    .filter((te) => selectedTimeIds.has(te.id))
    .reduce((sum, te) => sum + quantityOf(te) * Number(rateFor(te)), 0);
  const expenseSubtotal = expenses
    .filter((ex) => selectedExpenseIds.has(ex.id))
    .reduce((sum, ex) => sum + Number(ex.amount), 0);
  const manualSubtotal = manualLines.reduce(
    (sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_amount) || 0),
    0,
  );
  const subtotalPreview = timeSubtotal + expenseSubtotal + manualSubtotal;

  const taxNumber = Number(taxAmount);
  const taxValid = taxAmount.trim() === '' || (Number.isFinite(taxNumber) && taxNumber >= 0);
  const totalPreview = subtotalPreview + (taxValid && taxAmount.trim() !== '' ? taxNumber : 0);

  const nonEmptyManualLines = manualLines.filter((l) => l.description.trim() !== '');
  const lineCount = selectedTimeIds.size + selectedExpenseIds.size + nonEmptyManualLines.length;

  // Both tables are already filtered to exactly the billable set — unbilled
  // time, and expenses that are reimbursable + approved + unbilled — so "every
  // row shown" IS "everything not yet billed". The button flips to Clear once
  // it has nothing left to add.
  const allTimeSelected = timeEntries.length > 0 && timeEntries.every((te) => selectedTimeIds.has(te.id));
  const allExpensesSelected = expenses.length > 0 && expenses.every((ex) => selectedExpenseIds.has(ex.id));
  const toggleAllTime = () => {
    setSelectedTimeIds(allTimeSelected ? new Set() : new Set(timeEntries.map((te) => te.id)));
  };
  const toggleAllExpenses = () => {
    setSelectedExpenseIds(allExpensesSelected ? new Set() : new Set(expenses.map((ex) => ex.id)));
  };

  /**
   * The payload both buttons send, or null when the form isn't ready — the
   * side effect of reporting why is deliberate, so Preview and Create can
   * never disagree about what is valid.
   */
  const buildPayload = (): CreateInvoiceInput | null => {
    setTopError(null);
    setFieldErrors({});

    if (issueDate === null) {
      setFieldErrors({ issue_date: 'Pick an issue date' });
      return null;
    }
    if (dueDate === null) {
      setFieldErrors({ due_date: 'Pick a due date' });
      return null;
    }
    if (dueDate < issueDate) {
      setFieldErrors({ due_date: 'Due date must be on or after the issue date' });
      return null;
    }
    if (lineCount === 0) {
      setTopError('Add at least one line — select time entries, expenses, or add a manual/flat-fee line.');
      return null;
    }
    for (const l of nonEmptyManualLines) {
      if (!/^\d+(\.\d{1,2})?$/.test(l.quantity.trim()) || Number(l.quantity) <= 0) {
        setTopError(`Manual line "${l.description.trim()}" needs a valid quantity greater than 0 (e.g. 1 or 2.5).`);
        return null;
      }
      if (!/^-?\d+(\.\d{1,2})?$/.test(l.unit_amount.trim())) {
        setTopError(`Manual line "${l.description.trim()}" needs a valid unit price (e.g. 100.00).`);
        return null;
      }
    }

    const lines: InvoiceLineInput[] = [
      ...Array.from(selectedTimeIds).map((id): InvoiceLineInput => ({
        kind: 'time',
        time_entry_id: id,
        // Only when set by hand — otherwise the server prices from the sheet.
        ...(rateOverrides[id] ? { unit_amount: rateOverrides[id] } : {}),
      })),
      ...Array.from(selectedExpenseIds).map((id): InvoiceLineInput => ({ kind: 'expense', expense_id: id })),
      ...nonEmptyManualLines.map((l): InvoiceLineInput => ({
        kind: 'manual',
        description: l.description.trim(),
        quantity: l.quantity.trim() || '1',
        unit_amount: l.unit_amount.trim() || '0',
        ...(l.service_rate_name ? { service_rate_name: l.service_rate_name } : {}),
      })),
    ];

    return {
      project_id: projectId,
      issue_date: issueDate,
      due_date: dueDate,
      tax_amount: taxAmount.trim() ? taxNumber.toFixed(2) : undefined,
      notes: notes.trim() || undefined,
      show_discount: showDiscount,
      ...(typeof matterFilter === 'number' ? { matter_id: matterFilter } : {}),
      lines,
    };
  };

  /** Shared failure reporting — a preview hits the same 400/403/409s a create does. */
  const reportError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 400) {
      const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
      const fe = body?.details?.fieldErrors;
      if (fe) {
        const flat: Record<string, string> = {};
        for (const [k, v] of Object.entries(fe)) {
          if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
        }
        setFieldErrors(flat);
      }
      setTopError(err.message || 'Please fix the highlighted fields.');
      return;
    }
    if (err instanceof ApiError && err.status === 403) {
      setTopError("You don't have permission to create invoices on this project.");
      return;
    }
    if (err instanceof ApiError && err.status === 409) {
      setTopError(err.message || 'One or more selected items were already invoiced. Refresh and try again.');
      return;
    }
    setTopError(fallback);
  };

  // Renders the document this form would produce, without creating it. Worth
  // having because the alternative is to commit a draft and void it, and a
  // void is audited and refuses outright once a payment exists.
  const onPreview = async () => {
    const payload = buildPayload();
    if (payload === null) return;

    setPreviewing(true);
    try {
      const blob = await previewInvoicePdf(payload);
      setPreview({
        downloadUrl: '', // unused: `blob` short-circuits the fetch
        filename: `${project.case_number}-invoice-preview.pdf`,
        mimeType: 'application/pdf',
        blob,
      });
    } catch (err) {
      reportError(err, 'Could not render the preview. Please try again.');
    } finally {
      setPreviewing(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = buildPayload();
    if (payload === null) return;

    try {
      const { invoice } = await createInvoice.mutateAsync(payload);
      navigate(`/invoices/${invoice.id}`);
    } catch (err) {
      reportError(err, 'Could not create the invoice. Please try again.');
    }
  };

  return (
    <form className="niv-form" onSubmit={onSubmit} noValidate>
      <div className="card niv-project-banner">
        <div>
          <div className="niv-project-label">Project</div>
          <div className="niv-project-value">
            <code>{project.case_number}</code> {project.title}
          </div>
        </div>
        <button type="button" className="btn-text" onClick={onChangeProject}>
          Change project
        </button>
      </div>

      {(matters.length > 0 || matterFilter !== 'all') && (
        <div className="card">
          <label className="niv-field niv-matter-field">
            <span>Matter</span>
            <select
              className="form-input"
              aria-label="Matter"
              value={String(matterFilter)}
              onChange={(e) => {
                const v = e.target.value;
                const next: MatterFilter = v === 'all' || v === 'none' ? v : Number(v);
                setMatterFilter(next);
                // Drop anything selected that the new filter hides — it would
                // otherwise ride along invisibly (and a matter's invoice would
                // refuse it).
                const entries = timeEntriesQuery.data?.time_entries ?? [];
                const exps = expensesQuery.data?.expenses ?? [];
                setSelectedTimeIds((prev) => new Set([...prev].filter((id) =>
                  matterFilterMatches(next, entries.find((t) => t.id === id)?.matter_id ?? null))));
                setSelectedExpenseIds((prev) => new Set([...prev].filter((id) =>
                  matterFilterMatches(next, exps.find((x) => x.id === id)?.matter_id ?? null))));
              }}
            >
              <option value="all">All work on this project</option>
              {matters.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.closed_at !== null ? ' (closed)' : ''}
                </option>
              ))}
              <option value="none">Not in a matter</option>
            </select>
            <span className="niv-field-hint">
              {typeof matterFilter === 'number'
                ? 'Only this matter\'s time and expenses are offered, and its name prints on the invoice.'
                : 'Pick a matter to bill one job on its own invoice.'}
            </span>
          </label>
        </div>
      )}

      <div className="card">
        <div className="niv-section-head">
          <h3 className="niv-section-title">Time Entries</h3>
          {timeEntries.length > 0 && (
            <button type="button" className="btn-charcoal-secondary niv-select-all" onClick={toggleAllTime}>
              {allTimeSelected ? 'Clear selection' : `Select all ${timeEntries.length}`}
            </button>
          )}
        </div>
        {timeEntriesQuery.error ? (
          <p className="niv-status niv-error">Failed to load time entries.</p>
        ) : timeEntries.length === 0 ? (
          <p className="niv-status">No unbilled time entries on this project.</p>
        ) : (
          <table className="niv-table niv-pick-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Service</th>
                <th>Description</th>
                <th className="niv-numeric">Hours</th>
                <th className="niv-numeric">Rate</th>
                <th className="niv-numeric">Amount</th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.map((te) => {
                const rate = rateFor(te);
                const rowAmount = quantityOf(te) * Number(rate);
                const on = selectedTimeIds.has(te.id);
                // The entry points at one rate row; the project's sheet may say
                // another. Show what it WILL bill at, and what it was logged at
                // when those differ — silently re-pricing is worse than the bug.
                const logged = te.flat_amount ?? te.service_rate_amount ?? '0';
                const repriced = Number(rate) !== Number(logged);
                const overridden = rateOverrides[te.id] !== undefined;
                return (
                  <tr
                    key={te.id}
                    className={on ? 'table-row-selected' : undefined}
                    onClick={() => toggleTimeId(te.id)}
                    onKeyDown={rowKeyHandler(() => toggleTimeId(te.id))}
                    tabIndex={0}
                    aria-selected={on}
                  >
                    <td>{fmtDate(te.date)}</td>
                    <td>{te.user_display_name ?? `User #${te.user_id}`}</td>
                    <td>
                      {te.service_rate_name ?? `Rate #${te.service_rate_id}`}
                      {matterFilter === 'all' && te.matter_name && (
                        <span className="niv-rate-note">{te.matter_name}</span>
                      )}
                    </td>
                    <td className="niv-cell-desc">{te.description}</td>
                    <td className="niv-numeric">
                      {fmtEntryHours(te.hours)}
                      {/* Bills once — the hours are a record, not the quantity. */}
                      {te.service_rate_type === 'flat_fee' && <span className="niv-rate-note">flat fee</span>}
                    </td>
                    <td
                      className="niv-numeric niv-rate-cell"
                      // The rate control is a control, not part of the row toggle.
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {editingRateId === te.id ? (
                        <span className="niv-rate-edit">
                          <MoneyInput
                            className="niv-rate-input"
                            value={rate}
                            autoFocus
                            onChange={(next) => setRateOverrides((prev) => {
                              const copy = { ...prev };
                              // Back to the sheet price clears the override rather
                              // than pinning the same number.
                              if (next === '' || Number(next) === Number(billedRate(te))) delete copy[te.id];
                              else copy[te.id] = next;
                              return copy;
                            })}
                            aria-label={`Rate for ${te.service_rate_name ?? 'this entry'}`}
                          />
                          <IconAction
                            action="save"
                            iconSize={14}
                            label="Done editing rate"
                            onClick={() => setEditingRateId(null)}
                          />
                        </span>
                      ) : (
                        <span className="niv-rate-edit">
                          {formatMoney(rate)}
                          <IconAction
                            action="edit"
                            iconSize={14}
                            label={`Edit rate for ${te.service_rate_name ?? 'this entry'}`}
                            onClick={() => setEditingRateId(te.id)}
                          />
                        </span>
                      )}
                      {overridden ? (
                        <span className="niv-rate-note">overridden</span>
                      ) : repriced ? (
                        <span className="niv-rate-note" title="This entry points at a different rate row; the project's sheet is what bills.">
                          was {formatMoney(logged)}
                        </span>
                      ) : null}
                    </td>
                    <td className="niv-numeric">{formatMoney(rowAmount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="niv-section-head">
          <h3 className="niv-section-title">Reimbursable Expenses</h3>
          {expenses.length > 0 && (
            <button type="button" className="btn-charcoal-secondary niv-select-all" onClick={toggleAllExpenses}>
              {allExpensesSelected ? 'Clear selection' : `Select all ${expenses.length}`}
            </button>
          )}
        </div>
        {expensesQuery.error ? (
          <p className="niv-status niv-error">Failed to load expenses.</p>
        ) : expenses.length === 0 ? (
          <p className="niv-status">No approved, unbilled reimbursable expenses on this project.</p>
        ) : (
          <table className="niv-table niv-pick-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Vendor / notes</th>
                <th className="niv-numeric">Amount</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((ex) => {
                const on = selectedExpenseIds.has(ex.id);
                return (
                  <tr
                    key={ex.id}
                    className={on ? 'table-row-selected' : undefined}
                    onClick={() => toggleExpenseId(ex.id)}
                    onKeyDown={rowKeyHandler(() => toggleExpenseId(ex.id))}
                    tabIndex={0}
                    aria-selected={on}
                  >
                    <td>{fmtDate(ex.date)}</td>
                    <td>
                      {ex.type}
                      {matterFilter === 'all' && ex.matter_name && (
                        <span className="niv-rate-note">{ex.matter_name}</span>
                      )}
                    </td>
                    <td className="niv-cell-desc">{ex.vendor ?? ex.notes ?? '—'}</td>
                    <td className="niv-numeric">{formatMoney(ex.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h3 className="niv-section-title">Manual &amp; Flat-Fee Lines</h3>
        {flatFeeRates.length > 0 && (
          <div className="niv-flatfee-list">
            <span className="niv-items-label">Flat-fee rates for this project</span>
            {flatFeeRates.map((rate) => (
              <div key={rate.id} className="niv-flatfee-row">
                <span>{rate.name} — {formatMoney(rate.amount)}</span>
                <button type="button" className="niv-link" onClick={() => addFlatFeeLine(rate)}>
                  + add
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="niv-items">
          <span className="niv-items-label">Manual lines</span>
          {manualLines.map((l, idx) => (
            <div key={idx} className="niv-item-row">
              <input
                className="form-input"
                placeholder="Description"
                value={l.description}
                maxLength={500}
                onChange={(e) => updateManualLine(idx, { description: e.target.value })}
              />
              <input
                className="form-input niv-num"
                placeholder="Qty"
                inputMode="decimal"
                value={l.quantity}
                onChange={(e) => updateManualLine(idx, { quantity: e.target.value })}
              />
              <MoneyInput
                className="niv-num"
                placeholder="Unit price"
                allowNegative
                value={l.unit_amount}
                onChange={(next) => updateManualLine(idx, { unit_amount: next })}
              />
              <button type="button" className="niv-link" onClick={() => removeManualLine(idx)}>
                remove
              </button>
            </div>
          ))}
          <button type="button" className="niv-link" onClick={addManualLine}>
            + add line
          </button>
        </div>
      </div>

      <div className="card">
        <h3 className="niv-section-title">Invoice Details</h3>
        <div className="niv-row">
          <label className="niv-field">
            <span>Issue date *</span>
            <DateInput value={issueDate} onChange={onChangeIssueDate} required />
            {fieldErrors['issue_date'] && <span className="field-error">{fieldErrors['issue_date']}</span>}
          </label>
          <label className="niv-field">
            <span>Due date *</span>
            <DateInput
              value={dueDate}
              onChange={(next) => { setDueTouched(true); setDueDate(next); }}
              required
            />
            {!dueTouched && (
              <span className="niv-field-hint">Net {NET_DAYS} — follows the issue date until you change it.</span>
            )}
            {fieldErrors['due_date'] && <span className="field-error">{fieldErrors['due_date']}</span>}
          </label>
        </div>

        <div className="niv-row">
          <label className="niv-field">
            <span>Tax (USD)</span>
            <MoneyInput
              value={taxAmount}
              onChange={setTaxAmount}
              placeholder="0.00"
            />
            {fieldErrors['tax_amount'] && <span className="field-error">{fieldErrors['tax_amount']}</span>}
          </label>
          <div className="niv-field niv-subtotal-group">
            <span>Live preview</span>
            <p className="niv-total-preview">
              Subtotal: <strong>{formatMoney(subtotalPreview)}</strong>
              {' · '}Total: <strong>{formatMoney(totalPreview)}</strong>
            </p>
          </div>
        </div>

        <Toggle
          checked={showDiscount}
          onChange={setShowDiscount}
          label="Show discount vs. standard rate"
          description="Prints a DISC column on the invoice with each line's % below the Global rate."
          className="niv-field niv-field-full toggle-field"
        />

        <label className="niv-field niv-field-full">
          <span>Notes</span>
          <textarea
            className="form-input niv-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            placeholder="Optional notes to print on the invoice…"
          />
        </label>

        {topError && (
          <p className="niv-error-banner" role="alert">
            {topError}
          </p>
        )}

        <div className="niv-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/invoices')}
            disabled={createInvoice.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => void onPreview()}
            disabled={previewing || createInvoice.isPending || lineCount === 0}
          >
            {previewing ? 'Rendering…' : 'Preview PDF'}
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={createInvoice.isPending || lineCount === 0}
          >
            {createInvoice.isPending ? 'Creating…' : 'Create draft invoice'}
          </button>
        </div>
      </div>

      {preview && (
        <DocumentPreviewModal file={preview} onClose={() => setPreview(null)} />
      )}
    </form>
  );
}
